import { CAT_COLORS } from '../LandingPages/Portfolio/fixedIncomePortfolioConstants';

/**
 * Curated showcase scenarios — shaped like PortfolioEngine API responses.
 * Values representative of engine output for conservative / moderate / balanced profiles.
 */
export const PORTFOLIO_SHOWCASE_SCENARIOS = [
  {
    id: 'conservative',
    persona: 'Retiree seeking capital safety',
    pillClass: 'sb-fipb-pill-c',
    inputs: {
      risk_profile: 'Conservative — Rakshak',
      investment_amount_inr: 500000,
      horizon: 'Short — 1 to 3 years',
      rbi_rate_view: 'Rate hike cycle',
      tax_bracket: 'High — 30%+',
      investor_type: 'Retail',
      esg_preference: false,
    },
    profile_label: 'Capital Preservation',
    profile_label_hindi: 'Rakshak (रक्षक)',
    metrics: {
      total_value_inr: 500000,
      weighted_yield_pct: 6.82,
      annual_income_inr: 34100,
      weighted_duration_years: 2.1,
      estimated_max_drawdown_pct: 2.4,
      number_of_instruments: 5,
      category_distribution: {
        'G-Sec': 45.0,
        'T-Bill': 30.0,
        SDL: 15.0,
        'Bank FD / CD': 10.0,
      },
    },
    holdings: [
      { name: '91-day T-Bill', category: 'T-Bill', weight_pct: 30.0, indicative_yield: 6.65 },
      { name: '10-year G-Sec 7.18% 2034', category: 'G-Sec', weight_pct: 28.0, indicative_yield: 6.95 },
      { name: 'Maharashtra SDL 7.42% 2027', category: 'SDL', weight_pct: 15.0, indicative_yield: 7.12 },
    ],
    ai_snippet:
      'With a rate-hike outlook and short horizon, the engine tilts heavily toward sovereign and near-money instruments. Duration is kept below 2.5 years to limit mark-to-market sensitivity while preserving predictable coupon income.',
  },
  {
    id: 'moderate',
    persona: 'Professional building steady income',
    pillClass: 'sb-fipb-pill-m',
    inputs: {
      risk_profile: 'Moderate — Santulit',
      investment_amount_inr: 1000000,
      horizon: 'Medium — 3 to 7 years',
      rbi_rate_view: 'Rate pause / neutral',
      tax_bracket: 'Medium — 20–30%',
      investor_type: 'Retail',
      esg_preference: false,
    },
    profile_label: 'Balanced Income',
    profile_label_hindi: 'Santulit (संतुलित)',
    metrics: {
      total_value_inr: 1000000,
      weighted_yield_pct: 7.45,
      annual_income_inr: 74500,
      weighted_duration_years: 4.8,
      estimated_max_drawdown_pct: 5.2,
      number_of_instruments: 7,
      category_distribution: {
        'G-Sec': 25.0,
        'PSU Bond': 30.0,
        'NCD / Corp Bond': 25.0,
        SDL: 12.0,
        'Debt Mutual Fund': 8.0,
      },
    },
    holdings: [
      { name: 'NHAI 7.55% Bond 2029', category: 'PSU Bond', weight_pct: 18.0, indicative_yield: 7.55 },
      { name: 'REC 7.64% NCD 2028', category: 'PSU Bond', weight_pct: 12.0, indicative_yield: 7.64 },
      { name: 'HDFC Ltd 7.35% NCD 2030', category: 'NCD / Corp Bond', weight_pct: 15.0, indicative_yield: 7.35 },
    ],
    ai_snippet:
      'A neutral RBI stance supports a barbell between high-quality PSU paper and select AAA corporates. The allocation balances yield pickup over G-Secs with a weighted duration under 5 years — suitable for medium-term income goals.',
  },
  {
    id: 'balanced',
    persona: 'HNI with ESG preference',
    pillClass: 'sb-fipb-pill-b',
    inputs: {
      risk_profile: 'Balanced — Vikassheel',
      investment_amount_inr: 2000000,
      horizon: 'Long — 7 to 15 years',
      rbi_rate_view: 'Rate cut cycle',
      tax_bracket: 'High — 30%+',
      investor_type: 'HNI',
      esg_preference: true,
    },
    profile_label: 'Balanced Growth',
    profile_label_hindi: 'Vikassheel (विकासशील)',
    metrics: {
      total_value_inr: 2000000,
      weighted_yield_pct: 8.03,
      annual_income_inr: 160600,
      weighted_duration_years: 5.5,
      estimated_max_drawdown_pct: 9.0,
      number_of_instruments: 9,
      category_distribution: {
        'G-Sec': 10.0,
        'PSU Bond': 22.0,
        'NCD / Corp Bond': 35.0,
        SDL: 8.0,
        'InvIT / REIT': 12.0,
        'Sovereign Gold Bond': 8.0,
        'Debt Mutual Fund': 5.0,
      },
    },
    holdings: [
      { name: 'NHAI 7.55% Bond 2029', category: 'PSU Bond', weight_pct: 15.0, indicative_yield: 7.55 },
      { name: 'Green Infra NCD 8.25% 2031', category: 'NCD / Corp Bond', weight_pct: 20.0, indicative_yield: 8.25 },
      { name: 'IndiGrid InvIT — Series V', category: 'InvIT / REIT', weight_pct: 12.0, indicative_yield: 8.9 },
    ],
    ai_snippet:
      'ESG preference and a rate-cut view justify extending duration and adding green infrastructure NCDs plus InvIT exposure. Yield is prioritised within an investment-grade envelope, with sovereign anchors for stability.',
  },
];

export { CAT_COLORS };

export function fmtInrShort(n) {
  const num = Number(n) || 0;
  if (num >= 10000000) return `₹${(num / 10000000).toFixed(1)}Cr`;
  if (num >= 100000) return `₹${(num / 100000).toFixed(1)}L`;
  return `₹${Math.round(num).toLocaleString('en-IN')}`;
}

export function fmtInr(n) {
  return `₹${Math.round(Number(n) || 0).toLocaleString('en-IN')}`;
}

export function fmtPct(n) {
  return `${Number(n).toFixed(2)}%`;
}
